import { useEffect, useRef, useState } from "react";
import BottomSheet from "../common/BottomSheet";

// Compare page — global SEASON selector (seasons only; the resolved team is
// shown per player column — Marin follow-up 2026-08-14).
// Desktop: compact dropdown at the right of the Statistics header (frame 1b).
// Mobile: full-width trigger card opening the shared BottomSheet (frame 1m).
// One options source for both (seasonOptions.js builds it).

const OptionRow = ({ option, active, onClick, mobile }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center justify-between text-left ${
      mobile
        ? "px-4 py-[13px] border-t border-[#F1F5F9] text-[13.5px]"
        : "px-[10px] py-2 rounded-lg text-[12.5px] hover:bg-[#F5F8FC]"
    }`}
    style={{
      fontWeight: active ? 700 : 550,
      color: active ? "#1B6FD6" : "#334155",
    }}
  >
    <span data-no-translate="true">{option.label}</span>
    <span className={mobile ? "text-[12px]" : "text-[11px]"}>{active ? "✓" : ""}</span>
  </button>
);

const SeasonSelector = ({ options, selectedId, onSelect, variant = "desktop" }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = (options || []).find((o) => o.id === selectedId) || null;

  // Close the desktop dropdown on outside click.
  useEffect(() => {
    if (!open || variant !== "desktop") return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, variant]);

  if (!options || !options.length) return null;

  const pick = (id) => {
    onSelect(id);
    setOpen(false);
  };

  if (variant === "mobile") {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-between px-[13px] py-[11px] bg-white border border-[#E4E9F0] rounded-xl"
        >
          <span className="flex flex-col gap-[2px] text-left">
            <span className="text-[9.5px] font-bold tracking-[.08em] uppercase text-[#9AA7B8]">
              Season
            </span>
            <span className="text-[14.5px] font-semibold text-[#0F172A]" data-no-translate="true">
              {selected ? selected.label : "—"}
            </span>
          </span>
          <span className="text-[9px] text-[#7A8798]">▼</span>
        </button>
        <BottomSheet open={open} onClose={() => setOpen(false)} title="Season">
          <div className="pb-2 -mx-[18px]">
            {options.map((o) => (
              <OptionRow
                key={o.id}
                option={o}
                active={o.id === selectedId}
                onClick={() => pick(o.id)}
                mobile
              />
            ))}
          </div>
        </BottomSheet>
      </>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-[9px] px-[11px] py-[7px] border border-[#DDE4EE] rounded-[9px] bg-white hover:border-[#BCCBDD]"
      >
        <span className="text-[9.5px] font-bold tracking-[.08em] uppercase text-[#9AA7B8]">
          Season
        </span>
        <span className="text-[13.5px] font-semibold text-[#0F172A]" data-no-translate="true">
          {selected ? selected.label : "—"}
        </span>
        <span className="text-[8px] text-[#7A8798]">▼</span>
      </button>
      {open && (
        <div className="absolute right-0 top-[44px] w-[180px] max-h-[320px] overflow-y-auto bg-white border border-[#E4E9F0] rounded-[11px] shadow-[0_18px_40px_-14px_rgba(16,24,40,.28)] p-[6px] z-30">
          {options.map((o) => (
            <OptionRow
              key={o.id}
              option={o}
              active={o.id === selectedId}
              onClick={() => pick(o.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SeasonSelector;
